import { useEffect, useRef, useState } from "react";

type Step = { title: string; desc: string };

const ICONS = ["ri-customer-service-2-line", "ri-draft-line", "ri-tools-line", "ri-shield-check-line", "ri-hand-heart-line"];

// 스크롤 스토리텔링: 왼쪽 단계 목록은 고정(sticky)되고, 오른쪽 단계 카드를 스크롤하면 현재 단계가 강조됨.
// 모바일(lg 미만)에서는 목록 없이 카드만 순서대로 표시
export default function ProcessStory({ steps }: { steps: Step[] }) {
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    const els = refs.current.filter(Boolean) as HTMLLIElement[];
    if (!els.length || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      (entries) => {
        // 화면 중앙 띠에 들어온 카드를 현재 단계로
        for (const en of entries) if (en.isIntersecting) setActive(Number((en.target as HTMLElement).dataset.step));
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [steps.length]);

  const scrollTo = (i: number) => refs.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* 고정 단계 목록 (데스크톱) */}
      <ol className="hidden lg:block lg:col-span-4 sticky top-28 self-start space-y-1">
        {steps.map((s, i) => {
          const on = i === active;
          return (
            <li key={s.title}>
              <button
                type="button"
                onClick={() => scrollTo(i)}
                className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg text-left transition-colors cursor-pointer ${
                  on ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                <span className={`font-heading text-sm font-bold w-7 shrink-0 ${on ? "text-accent-300" : "text-foreground-400"}`}>0{i + 1}</span>
                <span className="font-heading font-semibold text-sm md:text-base">{s.title}</span>
              </button>
            </li>
          );
        })}
        <li className="pt-4 px-4">
          <div className="h-1 rounded-full bg-background-200 overflow-hidden">
            <div className="h-full bg-primary-500 transition-all duration-500" style={{ width: `${((active + 1) / steps.length) * 100}%` }}></div>
          </div>
        </li>
      </ol>

      {/* 단계 카드 */}
      <ol className="lg:col-span-8 space-y-5">
        {steps.map((s, i) => {
          const on = i === active;
          return (
            <li
              key={s.title}
              data-step={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              className={`glow-card rounded-xl bg-foreground-950 text-background-50 p-6 md:p-8 flex gap-5 md:gap-7 items-start transition-all duration-500 ${
                on ? "opacity-100 scale-100" : "lg:opacity-50 lg:scale-[0.99]"
              }`}
            >
              <div className="flex flex-col items-center gap-3 shrink-0">
                <span className="font-heading text-3xl md:text-4xl font-bold text-accent-300 leading-none">0{i + 1}</span>
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-background-50/10 text-accent-200">
                  <i className={`${ICONS[i % ICONS.length]} text-xl`}></i>
                </span>
              </div>
              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-sm md:text-base text-foreground-300 leading-relaxed max-w-2xl">{s.desc}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
